import { generatePacksSizesByMissionsAddresses } from '../../Utils/Inventory'
import { getPostcodesForBoundaries } from '../../Utils/Map'
import { numberWithCommas } from './../../Utils/Formatters'
import { getNumberOfHouseholdsInPolygonBulk } from '../../Module/graphQL/PathFinder/pathFinderGQL'
import { closestPoint } from '../../Containers/Campaigns/CreateEditCampaign/sideEffects'
import {
  getPolygonCentreAndDelta,
  getPolygonAreaInKm2,
  getTimeNeededForFlyersDistribution,
} from '../../shared/libraries/geo'
import { PAYMENT_SYSTEM_TYPES_ENUM } from '../../shared/constants'
import { roundTwoDecimals } from 'shared/libraries/utils/number'
import { downloadStringAsFile } from '../../Utils/Misc/index'
import { escapeCsvString } from '../../shared/csv'
import { splitArrayIntoChunks } from '../../shared/libraries/utils/array'

/**
 * Formats table value so if some of the vars is not provided "-" is shown
 * @param {*} val Value that should be formatted
 */
export const formatValue = val => {
  if (
    val === null ||
    val === undefined ||
    val === Infinity ||
    (typeof val === 'number' && isNaN(val))
  ) {
    return '-'
  }

  if (!isNaN(val)) {
    return numberWithCommas(
      Math.round(val * 100) / 100, // Round to 2 decimals
    )
  }

  return val
}

/**
 * Adds currency sign to value
 * Only adds currency sign to numbers
 * @param {number} val Number to which sign will be added
 * @param {string} currencySign
 */
export const addCurrencySign = (val, currencySign) =>
  val === '-' ? val : `${currencySign}${val}`

export const getCampaignTotals = function({ data }) {
  const columns = Object.keys(data[0])
  return data.reduce((acc, row, index) => {
    columns.forEach(columnName => {
      const value = Number(row[columnName])
      if (!isNaN(value) && value !== Infinity) {
        acc[columnName] = acc[columnName] ? acc[columnName] + value : value
      } else {
        acc[columnName] = 0
      }
    })
    return acc
  }, {})
}

/**
 * Returns table's footer with totals
 * @param {array} data Table's data
 */
export const getFooterData = ({
  data,
  bonusEnabled,
  bonusLimit,
  currencySign,
}) => {
  if (!data || !data.length) return []

  const footerData = getCampaignTotals({ data })

  const bonusCost = bonusEnabled
    ? `${currencySign}${formatValue(bonusLimit / 100)}`
    : '-'

  if (footerData['Total'] == undefined) {
    var total = '-'
  } else {
    var total = footerData['Total']
    total = currencySign + formatValue(total)
  }

  const getAvgMetricsCpm = data =>
    Object.fromEntries(
      Object.entries(data)
        .map(item => item[0].includes('CPM') && [item[0], item[1]])
        .filter(item => item),
    )

  return [
    {
      ...formatTableColumn({
        row: {
          ...footerData,
          'City': 'Totals:',
          'Flyer Pack Size': '',
          'Density': '',
          ...getAvgMetricsCpm(footerData),
        },
        currencySign,
      }),
      'Bonus Cost': bonusCost,
      'Total': total,
    },
  ]
}

/**
 * Maps raw data to related table columns
 * @param {array} args.rowsData Raw data
 * @param {number} args.printingCostPerFlyer Campaign's printing cost per flyer
 * @param {number} args.distributionCost Campaign's distribution cost
 */
export const mapRawDataToTableColumns = ({
  data,
  printingCostPerFlyer,
  metrics,
  paymentState,
}) =>
  data.map(row => {
    const { missionsAmount, packSize, cityName, areaInKM2, noHouseholds } = row
    const totalFlyers = missionsAmount * packSize
    const totalPrintingCost = totalFlyers * printingCostPerFlyer

    let totalDistributionCost = '-'
    let totalCost = '-'

    if (paymentState.paymentSystem === PAYMENT_SYSTEM_TYPES_ENUM.PER_HOUR) {
      const hourlyValue = getTimeNeededForFlyersDistribution({
        areaSize: areaInKM2,
        householdsAmount: noHouseholds ? noHouseholds : 0,
      })
      const BApayout = hourlyValue * Number(paymentState.hourlyPayout)

      totalDistributionCost =
        BApayout * ((Number(paymentState.clientMarginPercent) + 100) / 100)

      totalCost = totalPrintingCost + totalDistributionCost
    } else {
      totalDistributionCost = paymentState.distributionCost * missionsAmount
      totalCost = totalPrintingCost + totalDistributionCost
    }

    const { metricsQuotes } = getMetricsQuote({
      totalFlyers,
      metrics,
      totalCost,
    })
    const density = noHouseholds / areaInKM2

    return {
      'City': cityName,
      'Density': formatValue(density),
      'Flyer Pack Size': packSize,
      'Total Flyers': totalFlyers,
      '# Missions': missionsAmount,
      'Total Packs': missionsAmount,
      'Total Dist Cost': roundTwoDecimals(totalDistributionCost),
      'Total Printing Cost': totalPrintingCost,
      'Bonus Cost': null,
      'Total': roundTwoDecimals(totalCost),
      ...metricsQuotes,
    }
  })

/**
 * Calculates estimated target conversions and costs for each metric for group
 * Calculates total estimated conversions for group
 * @param {*} args.totalFlyers Total flyers for group
 * @param {*} args.metrics Campaign's metrics
 */
const getMetricsQuote = ({ totalFlyers, metrics, totalCost }) => {
  return metrics.reduce(
    (acc, metric) => {
      const estimatedTargetConversions =
        totalFlyers * metric.targetConversionRate
      acc.metricsQuotes[metric.name] = estimatedTargetConversions
      acc.metricsQuotes[`${metric.name} CPM`] = roundTwoDecimals(
        totalCost / estimatedTargetConversions,
      )
      acc.totalEstimatedConversions += estimatedTargetConversions
      return acc
    },
    { metricsQuotes: {}, totalEstimatedConversions: 0 },
  )
}

/**
 * Mutates the GeoJSON polygons with a 'packSize' prop on properties.
 */
export const addMissionPackSizes = function(geometries) {
  const packSizes = generatePacksSizesByMissionsAddresses({
    numbers: geometries.map(geo => geo.properties['noHouseholds']),
    maxGroups: 999999999,
    step: 100,
  })
  geometries.forEach((geo, i) => (geo.properties['packSize'] = packSizes[i]))
}

export const convertPolygonToPoint = polygon => {
  const centreAndDelta = getPolygonCentreAndDelta(
    polygon.coordinates[0].map(coords => ({
      longitude: coords[0],
      latitude: coords[1],
    })),
  )
  return {
    type: 'Point',
    coordinates: [centreAndDelta.longitude, centreAndDelta.latitude],
  }
}

/**
 * Adds the closest city name to geometry properties. Also adds the 'cityId'
 * for convenience
 * @param {string} cities[0].name
 * @param {GeoJSONPoint} cities[0].point
 */
export const addClosestCities = function({ features, cities }) {
  var cityPoints = cities.map(c => c.point)
  var closestPoints = features.map(f =>
    closestPoint(convertPolygonToPoint(f.geometry), cityPoints),
  )
  closestPoints.forEach((p, i) => {
    var { name: cityName, id: cityId } = cities[
      cityPoints.findIndex(lp => lp == p)
    ]
    features[i].properties['cityName'] = cityName
    features[i].properties['cityId'] = cityId
  })
}

/**
 * Aggregates an array of objects by common keys. When aggregating on more
 * than one key, the duplicate values will have #0, #1 and #2, etc appended
 * to the value.
 * @param {Array<object>} objs
 * @param {Array<string>} aggregateFields The keys to aggregate on
 */
export const aggregate = function(receivedObjs, aggregateFields) {
  const objs = [...receivedObjs]
  var aggregatedObjs = objs.reduce((aggRows, obj) => {
    var matchingRow = aggRows.find(dto => {
      return aggregateFields.every(key => {
        return dto[key] == obj[key]
      })
    })
    if (matchingRow) {
      Object.keys(obj).forEach(key => {
        if (aggregateFields.includes(key)) return // Skip aggregate columns
        if (typeof matchingRow[key] == 'number') {
          matchingRow[key] += obj[key]
        }
      })
    } else {
      aggRows.push(obj)
    }
    return aggRows
  }, [])

  // Sort by aggregated fields so that the method of appending #i works below
  aggregatedObjs.sort((a, b) => {
    var i = 0
    do {
      let key = aggregateFields[i]
      i++
      if (key === undefined) break
      if (a[key] < b[key]) return -1
      else if (a[key] > b[key]) return 1
    } while (true)
    return 0
  })

  // This part of the code adds the hash/index identifiers to aggregate rows
  // that duplicate
  // e.g.
  // { cityName: 'Sydney', packSize: 500, }
  // { cityName: 'Sydney', packSize: 600, }
  // becomes
  // { cityName: 'Sydney #0', packSize: 500, }
  // { cityName: 'Sydney #1', packSize: 600, }
  if (aggregateFields.length > 2)
    throw new Error('Not yet supported, write some code and add tests.')
  if (aggregateFields.length == 1) return aggregatedObjs
  var copy = [],
    lastKeyValue = null,
    lastDupeIndex = null
  do {
    var aggKey = aggregateFields[0]
    var temp = { ...aggregatedObjs.shift() }
    var keyValue = temp[aggKey]
    if (keyValue != lastKeyValue) {
      temp[aggKey] = keyValue + ' #0'
      lastDupeIndex = 0
      lastKeyValue = keyValue
    } else {
      lastDupeIndex++
      temp[aggKey] = keyValue + ' #' + lastDupeIndex
    }
    copy.push(temp)
  } while (aggregatedObjs.length)
  return copy
}

/**
 * Fetches all the mission data required for displaying a quote. Returns an
 * array of Geo JSON features and populated properties with
 * `packSize`, `cityName` and `noHouseholds`.
 * @param {Array<Array<LatLng>>} args.overlays Campaign's mission areas (both new and already existing)
 * @param {string} args.countryCode Campaign's country code
 * @param {object} args.citiesMap This is a hashmap of city id to city object.
 * per city.
 * @returns {Promise<Array<GeoJSONFeature>>}
 */
export const fetchMissionData = async ({
  overlays,
  countryCode,
  citiesMap,
}) => {
  var features = overlays.map(overlay => ({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          ...overlay.boundary.map(({ latitude, longitude }) => [
            longitude,
            latitude,
          ]),
          // First and last coords should be the same in GEOJSON
          [overlay.boundary[0].longitude, overlay.boundary[0].latitude],
        ],
      ],
    },
    properties: {
      id: overlay.id,
    },
  }))
  var cities = Object.values(citiesMap).map(dto => ({
    name: dto.name,
    id: dto.id,
    point: {
      type: 'Point',
      coordinates: [dto.location.longitude, dto.location.latitude],
    },
  }))

  const geometries = features.map(f => f.geometry)

  const dataChunks = splitArrayIntoChunks({
    inputArray: geometries,
    itemsPerChunk: 50,
  })
  const noHouseholds = []

  for (const chunk of dataChunks) {
    const result = await getNumberOfHouseholdsInPolygonBulk(countryCode, chunk)
    noHouseholds.push(...result)
  }

  features.forEach((f, i) => (f.properties['noHouseholds'] = noHouseholds[i]))
  addMissionPackSizes(features)
  addClosestCities({ features, cities })
  return features
}

/**
 * @typedef CityPackAggregation
 * @property {string} cityName Either City #0 or City #1 or City #2
 * @property {number} missionsAmount The number of missions to do in this city
 * with this pack size
 * @property {number} packSize Number of flyers in a pack for this mission group
 *
 * @typedef LatLng
 * @property {number} latitude
 * @property {number} longitude
 *
 * Aggregates all missions by city and then by pack size
 * @param {object} features PF data
 *
 * @returns {<Array<CityPackAggregation>>}
 */
export const aggregateMissionData = async features => {
  // Add a value for missionsAmount

  const aggregatedData = aggregate(
    features.map(f => {
      const areaInKM2 = getPolygonAreaInKm2(
        f.geometry.coordinates[0].map(([longitude, latitude]) => ({
          latitude,
          longitude,
        })),
      )
      return {
        ...f.properties,
        missionsAmount: 1,
        areaInKM2,
      }
    }),
    ['cityName', 'packSize'],
  )

  return aggregatedData
}

/**
 * Format the table columns.
 * Adds currency sign to the listed values
 */
export const formatTableColumn = ({ row, currencySign }) => {
  const columnsWithCurrency = [
    'Total Dist Cost',
    'Total Printing Cost',
    'Total',
  ]
  const costPerMetricPrefix = ' CPM'

  return Object.entries(row).reduce((acc, [key, value]) => {
    if (key == 'Bonus Cost') {
      acc[key] = '-'
      return acc
    } else if (
      columnsWithCurrency.includes(key) ||
      key.includes(costPerMetricPrefix)
    ) {
      acc[key] = addCurrencySign(formatValue(value), currencySign)
      return acc
    } else if (typeof value === 'number') {
      value = formatValue(value)
    } else if (!value) {
      value = '-'
    }
    acc[key] = value
    return acc
  }, {})
}

/**
 * Mutates the features to add the closest postcode to the mission as a
 * 'postcode' key in properties. If a postcode can't be matched it will set
 * it to the value of 'Unknown postcode'
 * @param {Array<GeoJSONFeature>} features
 */
const addPostcodesBulk = async function({ features, countryCode }) {
  var postcodes = await getPostcodesForBoundaries({
    geometries: features.map(f => f.geometry),
    countryCode,
  })
  features.forEach((f, i) => {
    f.properties.postcode = postcodes[i] ? postcodes[i] : 'Unknown postcode'
  })
}

/**   
 * City data information
 * @typedef CityData
 * @property {number} id
 * @property {string} name
 * @property {Object} location Center point of the city
 * @property {number} location.latitude
 * @property {number} location.longitude
 * /
/**   
 * Processed City data with postcodes and related missions overlays
 * @typedef CityPostcode
 * @property {number} householdsAmount Number of households for the postcode
 * @property {number} missionsAmount Number of missions for the postcode
 */

/**
 * Processed City data with postcodes and related missions overlays
 * @typedef CityDataWithPostcodes
 * @property {CityData} city City information
 * @property {number} households Number of households in the city
 * @property {number} missionsAmount Number of missions in the city
 * @property {Object.<string, CityPostcode>} postcodes Number of households and missions
 * for each postcode in the city.
 * Key is is postcode name, value is an object with number of households and missions.
 * @property {Object[][]} overlays list of overlays of the city
 */

/**
 * Convert campaign's mission boundaries into array of cities and
 * @param {Object} args
 * @param {Object[][]} args.overlays Campaign's mission areas (both new and already existing)
 * @param {String} args.countryCode Country code
 * @param {Object.<string, CityData>} args.citiesMap Cities information hash map
 * @returns {CityDataWithPostcodes[]}
 */
export const splitOverlaysIntoCitiesWithPostcodes = async ({
  overlays,
  countryCode,
  citiesMap,
}) => {
  var features = await fetchMissionData({
    overlays,
    countryCode,
    citiesMap,
  })
  // The only additional data we're missing is the postcode for each mission.
  await addPostcodesBulk({ features, countryCode })

  // Group by city to be backwards compatible with existing code
  var cities = {}
  features.forEach(f => {
    let p = f.properties
    let city = (cities[p.cityId] = cities[p.cityId] || {})
    city.city = { name: p.cityName }
    city.householdsAmount = city.householdsAmount || 0
    city.householdsAmount += p.noHouseholds || 0
    city.totalFlyers = city.totalFlyers || 0
    city.totalFlyers += p.packSize || 0
    city.missionsAmount = city.missionsAmount || 0
    city.missionsAmount++
    // Add the overlays
    city.overlays = city.overlays || []
    city.overlays.push({
      boundary: f.geometry.coordinates[0].map(dto => ({
        latitude: dto[1],
        longitude: dto[0],
      })),
    })
    city.postcodes = city.postcodes || {}
    let postcodeMatch = (city.postcodes[p.postcode] =
      city.postcodes[p.postcode] || {})
    postcodeMatch.householdsAmount = postcodeMatch.householdsAmount || 0
    postcodeMatch.householdsAmount += p.noHouseholds
    postcodeMatch.totalFlyers = postcodeMatch.totalFlyers || 0
    postcodeMatch.totalFlyers += p.packSize
    postcodeMatch.missionsAmount = postcodeMatch.missionsAmount || 0
    postcodeMatch.missionsAmount += 1
  })
  return Object.values(cities)
}

/**
 * Convert quote table data to csv file
 * @param {Array} headers
 * @param {Array} data
 */
export const initFileDownload = (headers, data, footerData, currencySign) => {
  const formatRow = (row, isFooter = false) => {
    return headers
      .map((key, index) => {
        let value = isFooter
          ? row[key]
          : formatTableColumn({ row, currencySign })[key]
        // Handle null/undefined
        if (value === null || value === undefined) {
          value = ''
        }
        return typeof value === 'string' ? escapeCsvString(value) : value
      })
      .join(',')
  }

  const csvContent = [
    // Headers
    headers.join(','),
    // Data rows
    ...data.map(row => formatRow(row)),
    // Footer rows
    ...footerData.map(row => formatRow(row, true)),
  ].join('\n')

  downloadStringAsFile(csvContent, 'quote-table.csv', 'text/csv')
}
